import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faFileAlt,
  faImages,
  faSave,
  faUpload,
  faTrash,
  faEdit,
  faCopy,
  faSpinner,
  faCheck,
  faExclamationTriangle,
  faPlus,
  faTimes,
  faBook,
  faNewspaper,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../utils/api';
import ResourceManager from './resourcemanager';
import './cmsmanager.css';

const CMSManager = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // 网站配置状态
  const [siteConfig, setSiteConfig] = useState({});
  const [configChanges, setConfigChanges] = useState({});
  
  // 页面内容状态
  const [pageContent, setPageContent] = useState({});
  const [selectedPage, setSelectedPage] = useState('home');
  const [editingContent, setEditingContent] = useState(null);
  const [contentForm, setContentForm] = useState({});
  
  // 媒体文件状态
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // 富文本编辑器配置
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent',
    'align', 'link', 'image', 'video'
  ];

  // 显示消息
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  // 加载网站配置
  const loadSiteConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/cms/config');
      if (response.data.status === 'success') {
        setSiteConfig(response.data.data.config);
      }
    } catch (error) {
      console.error('加载配置失败:', error);
      showMessage('error', '加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存网站配置
  const saveSiteConfig = async () => {
    try {
      setLoading(true);
      const configs = Object.entries(configChanges).map(([key, value]) => {
        const [configType, configKey] = key.split('.');
        return {
          configType,
          configKey,
          configValue: value
        };
      });
      
      if (configs.length === 0) {
        showMessage('warning', '没有需要保存的更改');
        return;
      }
      
      const response = await axios.put('/api/cms/config', { configs });
      if (response.data.status === 'success') {
        showMessage('success', '配置保存成功');
        setConfigChanges({});
        await loadSiteConfig();
      }
    } catch (error) {
      console.error('保存配置失败:', error);
      showMessage('error', '保存配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 更新配置值
  const updateConfigValue = (configType, configKey, value) => {
    const key = `${configType}.${configKey}`;
    setConfigChanges(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 获取配置值（优先使用更改中的值）
  const getConfigValue = (configType, configKey) => {
    const key = `${configType}.${configKey}`;
    if (configChanges[key] !== undefined) {
      return configChanges[key];
    }
    return siteConfig[configType]?.[configKey] || '';
  };

  // 加载页面内容
  const loadPageContent = async (pageId) => {
    try {
      setLoading(true);
      const response = await api.get('/admin/website-content');
      if (response.data.status === 'success') {
        setPageContent(response.data.data);
      }
    } catch (error) {
      console.error('加载页面内容失败:', error);
      showMessage('error', '加载页面内容失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存页面内容
  const savePageContent = async () => {
    try {
      setLoading(true);
      const response = await api.put('/admin/website-content', pageContent);
      if (response.data.status === 'success') {
        showMessage('success', '页面内容保存成功');
      }
    } catch (error) {
      console.error('保存页面内容失败:', error);
      showMessage('error', '保存页面内容失败');
    } finally {
      setLoading(false);
    }
  };

  // 编辑内容区块
  const editContentSection = (section, key) => {
    setEditingContent({ section, key });
    setContentForm(pageContent[section] || {});
  };

  // 保存内容区块
  const saveContentSection = () => {
    if (editingContent) {
      setPageContent(prev => ({
        ...prev,
        [editingContent.section]: contentForm
      }));
      setEditingContent(null);
      setContentForm({});
    }
  };

  // 取消编辑
  const cancelEdit = () => {
    setEditingContent(null);
    setContentForm({});
  };

  // 加载媒体文件
  const loadMediaFiles = async (fileType = 'image') => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/cms/media?fileType=${fileType}&limit=50`);
      if (response.data.status === 'success') {
        setMediaFiles(response.data.data.files || []);
      }
    } catch (error) {
      console.error('加载媒体文件失败:', error);
      showMessage('error', '加载媒体文件失败');
    } finally {
      setLoading(false);
    }
  };

  // 上传文件
  const uploadFile = async (file, metadata = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      Object.entries(metadata).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await axios.post('/api/cms/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.status === 'success') {
        showMessage('success', '文件上传成功');
        await loadMediaFiles();
        setUploadProgress(0);
      }
    } catch (error) {
      console.error('上传文件失败:', error);
      showMessage('error', '文件上传失败');
      setUploadProgress(0);
    }
  };

  // 删除媒体文件
  const deleteMediaFile = async (fileId) => {
    if (!window.confirm('确定要删除这个文件吗？')) {
      return;
    }
    
    try {
      const response = await axios.delete(`/api/cms/media/${fileId}`);
      if (response.data.status === 'success') {
        showMessage('success', '文件删除成功');
        await loadMediaFiles();
      }
    } catch (error) {
      console.error('删除文件失败:', error);
      showMessage('error', '删除文件失败');
    }
  };

  // 初始化数据
  const initializeData = async () => {
    if (!window.confirm('确定要初始化默认数据吗？这将重置所有配置！')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post('/api/cms/initialize');
      if (response.data.status === 'success') {
        showMessage('success', '数据初始化成功');
        await loadSiteConfig();
      }
    } catch (error) {
      console.error('初始化数据失败:', error);
      showMessage('error', '初始化数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时加载数据
  useEffect(() => {
    if (activeTab === 'config') {
      loadSiteConfig();
    } else if (activeTab === 'content') {
      loadPageContent(selectedPage);
    } else if (activeTab === 'media') {
      loadMediaFiles();
    }
  }, [activeTab, selectedPage]);

  // 渲染配置编辑器
  const renderConfigEditor = () => {
    const configTypes = {
      brand: '品牌信息',
      navigation: '导航菜单',
      hero: '首页轮播',
      features: '功能特性',
      footer: '页脚信息',
      contact: '联系信息',
      seo: 'SEO设置',
      theme: '主题配置',
      social: '社交媒体',
      business: '业务链接'
    };

    return (
      <div className="config-editor">
        <div className="config-header">
          <h3>网站配置管理</h3>
          <div className="config-actions">
            <button 
              onClick={saveSiteConfig} 
              disabled={loading || Object.keys(configChanges).length === 0}
              className="btn btn-primary"
            >
              {loading ? '保存中...' : '保存更改'}
            </button>
            <button onClick={initializeData} className="btn btn-secondary">
              初始化数据
            </button>
          </div>
        </div>

        {Object.keys(configChanges).length > 0 && (
          <div className="unsaved-changes">
            <span>有 {Object.keys(configChanges).length} 项未保存的更改</span>
          </div>
        )}

        <div className="config-sections">
          {Object.entries(configTypes).map(([type, label]) => (
            <div key={type} className="config-section">
              <h4>{label}</h4>
              <div className="config-fields">
                {renderConfigFields(type)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 渲染配置字段
  const renderConfigFields = (configType) => {
    const fields = getConfigFieldsForType(configType);
    
    return fields.map(field => (
      <div key={field.key} className="config-field">
        <label>{field.label}</label>
        {field.type === 'textarea' ? (
          <textarea
            value={getConfigValue(configType, field.key)}
            onChange={(e) => updateConfigValue(configType, field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={field.rows || 3}
          />
        ) : field.type === 'select' ? (
          <select
            value={getConfigValue(configType, field.key)}
            onChange={(e) => updateConfigValue(configType, field.key, e.target.value)}
          >
            {field.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : field.type === 'color' ? (
          <input
            type="color"
            value={getConfigValue(configType, field.key)}
            onChange={(e) => updateConfigValue(configType, field.key, e.target.value)}
          />
        ) : (
          <input
            type={field.type || 'text'}
            value={getConfigValue(configType, field.key)}
            onChange={(e) => updateConfigValue(configType, field.key, e.target.value)}
            placeholder={field.placeholder}
          />
        )}
        {field.description && (
          <small className="field-description">{field.description}</small>
        )}
      </div>
    ));
  };

  // 获取配置字段定义
  const getConfigFieldsForType = (configType) => {
    const fieldDefinitions = {
      brand: [
        { key: 'name', label: '品牌名称', placeholder: '输入品牌名称' },
        { key: 'slogan', label: '品牌标语', placeholder: '输入品牌标语' },
        { key: 'description', label: '品牌描述', type: 'textarea', placeholder: '输入品牌描述' },
        { key: 'logoUrl', label: 'Logo URL', placeholder: '输入Logo图片URL' },
        { key: 'logoAlt', label: 'Logo替代文本', placeholder: '输入Logo替代文本' }
      ],
      navigation: [
        { key: 'homeText', label: '首页文本', placeholder: '首页' },
        { key: 'aboutText', label: '关于我们文本', placeholder: '关于我们' },
        { key: 'servicesText', label: '服务文本', placeholder: '服务' },
        { key: 'newsText', label: '新闻文本', placeholder: '新闻' },
        { key: 'contactText', label: '联系我们文本', placeholder: '联系我们' }
      ],
      hero: [
        { key: 'autoPlay', label: '自动播放', type: 'select', options: [
          { value: 'true', label: '开启' },
          { value: 'false', label: '关闭' }
        ]},
        { key: 'interval', label: '切换间隔(秒)', type: 'number', placeholder: '5' },
        { key: 'showDots', label: '显示指示点', type: 'select', options: [
          { value: 'true', label: '显示' },
          { value: 'false', label: '隐藏' }
        ]}
      ],
      contact: [
        { key: 'phone', label: '联系电话', placeholder: '输入联系电话' },
        { key: 'email', label: '邮箱地址', placeholder: '输入邮箱地址' },
        { key: 'address', label: '公司地址', type: 'textarea', placeholder: '输入公司地址' },
        { key: 'workingHours', label: '工作时间', placeholder: '输入工作时间' }
      ],
      seo: [
        { key: 'title', label: '网站标题', placeholder: '输入网站标题' },
        { key: 'description', label: '网站描述', type: 'textarea', placeholder: '输入网站描述' },
        { key: 'keywords', label: '关键词', placeholder: '输入关键词，用逗号分隔' }
      ],
      theme: [
        { key: 'primaryColor', label: '主色调', type: 'color' },
        { key: 'secondaryColor', label: '辅助色', type: 'color' },
        { key: 'accentColor', label: '强调色', type: 'color' },
        { key: 'backgroundColor', label: '背景色', type: 'color' },
        { key: 'textColor', label: '文字颜色', type: 'color' }
      ]
    };
    
    return fieldDefinitions[configType] || [];
  };

  // 渲染媒体管理器
  const renderMediaManager = () => {
    return (
      <div className="media-manager">
        <div className="media-header">
          <h3>媒体文件管理</h3>
          <div className="media-actions">
            <input
              type="file"
              id="file-upload"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files[0]) {
                  uploadFile(e.target.files[0], {
                    description: '通过CMS上传',
                    usage: 'content'
                  });
                }
              }}
              style={{ display: 'none' }}
            />
            <label htmlFor="file-upload" className="btn btn-primary">
              上传文件
            </label>
          </div>
        </div>

        {uploadProgress > 0 && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <span>{uploadProgress}%</span>
          </div>
        )}

        <div className="media-grid">
          {mediaFiles.map(file => (
            <div key={file._id} className="media-item">
              <div className="media-preview">
                <img 
                  src={file.fileUrl} 
                  alt={file.altText || file.originalName}
                  onError={(e) => {
                    e.target.src = '/placeholder-image.png';
                  }}
                />
              </div>
              <div className="media-info">
                <h5>{file.originalName}</h5>
                <p>{file.description}</p>
                <div className="media-meta">
                  <span>类型: {file.fileType}</span>
                  <span>大小: {(file.fileSize / 1024).toFixed(1)}KB</span>
                  {file.dimensions && (
                    <span>尺寸: {file.dimensions.width}×{file.dimensions.height}</span>
                  )}
                </div>
                <div className="media-actions">
                  <button 
                    onClick={() => setSelectedFile(file)}
                    className="btn btn-sm btn-secondary"
                  >
                    编辑
                  </button>
                  <button 
                    onClick={() => deleteMediaFile(file._id)}
                    className="btn btn-sm btn-danger"
                  >
                    删除
                  </button>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(file.fileUrl);
                      showMessage('success', 'URL已复制到剪贴板');
                    }}
                    className="btn btn-sm btn-primary"
                  >
                    复制URL
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 渲染页面内容管理
  const renderContentEditor = () => {
    const contentSections = [
      { key: 'hero', label: '首页横幅', icon: faGlobe },
      { key: 'about', label: '关于我们', icon: faFileAlt },
      { key: 'services', label: '服务介绍', icon: faCog },
      { key: 'contact', label: '联系信息', icon: faNewspaper },
      { key: 'footer', label: '页脚信息', icon: faFileAlt }
    ];

    return (
      <div className="cms-content-editor">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">页面内容管理</h3>
            <p className="text-gray-600 mt-1">编辑网站各个页面的内容</p>
          </div>
          <button
            onClick={savePageContent}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            ) : (
              <FontAwesomeIcon icon={faSave} className="mr-2" />
            )}
            保存所有更改
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {contentSections.map(section => (
            <div key={section.key} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-medium text-gray-900 flex items-center">
                  <FontAwesomeIcon icon={section.icon} className="mr-2 text-blue-600" />
                  {section.label}
                </h4>
                <button
                  onClick={() => editContentSection(section.key)}
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <FontAwesomeIcon icon={faEdit} className="mr-1" />
                  编辑
                </button>
              </div>
              
              <div className="space-y-3">
                {pageContent[section.key] && Object.entries(pageContent[section.key]).map(([key, value]) => (
                  <div key={key} className="border-l-4 border-blue-200 pl-4">
                    <div className="text-sm font-medium text-gray-700 capitalize mb-1">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </div>
                    <div className="text-sm text-gray-600 line-clamp-3">
                      {typeof value === 'string' ? (
                        value.length > 100 ? `${value.substring(0, 100)}...` : value
                      ) : (
                        JSON.stringify(value)
                      )}
                    </div>
                  </div>
                ))}
                
                {(!pageContent[section.key] || Object.keys(pageContent[section.key]).length === 0) && (
                  <div className="text-gray-500 text-sm italic">
                    暂无内容，点击编辑添加内容
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 编辑模态框 */}
        {editingContent && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  编辑 {contentSections.find(s => s.key === editingContent.section)?.label}
                </h3>
                <button
                  onClick={cancelEdit}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto space-y-4">
                {renderContentFields(editingContent.section)}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={saveContentSection}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                >
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  保存
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 渲染内容字段
  const renderContentFields = (sectionKey) => {
    const fieldConfigs = {
      hero: [
        { key: 'title', label: '主标题', type: 'text', placeholder: '输入主标题' },
        { key: 'subtitle', label: '副标题', type: 'text', placeholder: '输入副标题' },
        { key: 'description', label: '描述', type: 'richtext', placeholder: '输入描述内容' },
        { key: 'buttonText', label: '按钮文字', type: 'text', placeholder: '输入按钮文字' },
        { key: 'buttonLink', label: '按钮链接', type: 'text', placeholder: '输入按钮链接' },
        { key: 'backgroundImage', label: '背景图片', type: 'text', placeholder: '输入图片URL' }
      ],
      about: [
        { key: 'title', label: '标题', type: 'text', placeholder: '输入标题' },
        { key: 'description', label: '描述', type: 'richtext', placeholder: '输入描述内容' },
        { key: 'mission', label: '使命', type: 'richtext', placeholder: '输入使命内容' },
        { key: 'vision', label: '愿景', type: 'richtext', placeholder: '输入愿景内容' },
        { key: 'values', label: '价值观', type: 'richtext', placeholder: '输入价值观内容' }
      ],
      services: [
        { key: 'title', label: '标题', type: 'text', placeholder: '输入标题' },
        { key: 'description', label: '描述', type: 'richtext', placeholder: '输入描述内容' },
        { key: 'features', label: '特色功能', type: 'richtext', placeholder: '输入特色功能介绍' }
      ],
      contact: [
        { key: 'title', label: '标题', type: 'text', placeholder: '输入标题' },
        { key: 'description', label: '描述', type: 'richtext', placeholder: '输入描述内容' },
        { key: 'address', label: '地址', type: 'text', placeholder: '输入联系地址' },
        { key: 'phone', label: '电话', type: 'text', placeholder: '输入联系电话' },
        { key: 'email', label: '邮箱', type: 'text', placeholder: '输入联系邮箱' },
        { key: 'workingHours', label: '工作时间', type: 'text', placeholder: '输入工作时间' }
      ],
      footer: [
        { key: 'companyName', label: '公司名称', type: 'text', placeholder: '输入公司名称' },
        { key: 'copyright', label: '版权信息', type: 'text', placeholder: '输入版权信息' },
        { key: 'description', label: '公司描述', type: 'richtext', placeholder: '输入公司描述' },
        { key: 'socialLinks', label: '社交链接', type: 'richtext', placeholder: '输入社交媒体链接' }
      ]
    };

    const fields = fieldConfigs[sectionKey] || [];

    return (
      <div className="space-y-4">
        {fields.map(field => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            {field.type === 'richtext' ? (
              <ReactQuill
                value={contentForm[field.key] || ''}
                onChange={(value) => setContentForm(prev => ({ ...prev, [field.key]: value }))}
                modules={quillModules}
                formats={quillFormats}
                className="bg-white"
                placeholder={field.placeholder}
              />
            ) : (
              <input
                type="text"
                value={contentForm[field.key] || ''}
                onChange={(e) => setContentForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder={field.placeholder}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="cms-manager">
      <div className="cms-header">
        <h2>内容管理系统</h2>
        {message.text && (
          <div className={`message message-${message.type}`}>
            {message.text}
          </div>
        )}
      </div>

      <div className="cms-tabs">
        <button 
          className={`tab ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          <FontAwesomeIcon icon={faCog} className="mr-2" />
          网站配置
        </button>
        <button 
          className={`tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
          页面内容
        </button>
        <button 
          className={`tab ${activeTab === 'resources' ? 'active' : ''}`}
          onClick={() => setActiveTab('resources')}
        >
          <FontAwesomeIcon icon={faBook} className="mr-2" />
          资源管理
        </button>
        <button 
          className={`tab ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          <FontAwesomeIcon icon={faImages} className="mr-2" />
          媒体管理
        </button>
      </div>

      <div className="cms-content">
        {loading && <div className="loading">加载中...</div>}
        
        {activeTab === 'config' && renderConfigEditor()}
        {activeTab === 'content' && renderContentEditor()}
        {activeTab === 'resources' && <ResourceManager />}
        {activeTab === 'media' && renderMediaManager()}
      </div>
    </div>
  );
};

export default CMSManager;